import { BISHOP_DIRS, KING_DELTAS, KNIGHT_DELTAS, ROOK_DIRS, pawnDirection, type Delta } from './moveGen.js';
import { BISHOP, EMPTY, KING, KNIGHT, NO_SQUARE, PAWN, QUEEN, ROOK, pieceCode, type Color, type Square } from './types.js';
import { SQUARE_COUNT, fileOf, rowOf, squareAt } from './squares.js';

function stepAttacked(board: Int8Array, sq: Square, deltas: readonly Delta[], attacker: number): boolean {
  const file = fileOf(sq);
  const row = rowOf(sq);
  for (const [df, dr] of deltas) {
    const from = squareAt(file + df, row + dr);
    if (from !== NO_SQUARE && board[from] === attacker) return true;
  }
  return false;
}

function rayAttacked(board: Int8Array, sq: Square, dirs: readonly Delta[], slider: number, queen: number): boolean {
  const file = fileOf(sq);
  const row = rowOf(sq);
  for (const [df, dr] of dirs) {
    for (let step = 1; ; step++) {
      const from = squareAt(file + df * step, row + dr * step);
      if (from === NO_SQUARE) break;
      const code = board[from] ?? EMPTY;
      if (code === EMPTY) continue;
      if (code === slider || code === queen) return true;
      break;
    }
  }
  return false;
}

/** Whether any piece of `by` attacks `sq` (occupancy of `sq` itself is ignored). */
export function isSquareAttacked(board: Int8Array, sq: Square, by: Color): boolean {
  // A pawn of `by` attacks diagonally forward, so it sits one row *behind* the target.
  const pawnRow = rowOf(sq) - pawnDirection(by);
  const pawn = pieceCode(PAWN, by);
  for (const df of [-1, 1]) {
    const from = squareAt(fileOf(sq) + df, pawnRow);
    if (from !== NO_SQUARE && board[from] === pawn) return true;
  }
  const queen = pieceCode(QUEEN, by);
  return (
    stepAttacked(board, sq, KNIGHT_DELTAS, pieceCode(KNIGHT, by)) ||
    stepAttacked(board, sq, KING_DELTAS, pieceCode(KING, by)) ||
    rayAttacked(board, sq, ROOK_DIRS, pieceCode(ROOK, by), queen) ||
    rayAttacked(board, sq, BISHOP_DIRS, pieceCode(BISHOP, by), queen)
  );
}

/** The king square of `color`, or NO_SQUARE if absent (only in malformed test boards). */
export function findKing(board: Int8Array, color: Color): Square {
  const king = pieceCode(KING, color);
  for (let sq = 0; sq < SQUARE_COUNT; sq++) {
    if (board[sq] === king) return sq;
  }
  return NO_SQUARE;
}

export function isInCheck(board: Int8Array, color: Color): boolean {
  const king = findKing(board, color);
  return king !== NO_SQUARE && isSquareAttacked(board, king, color === 'w' ? 'b' : 'w');
}
